/**
 * RBACRoute — 페이지그룹 권한(none/read/write)에 따라 라우트 진입을 막는 가드.
 *
 * - useAuth().can(pageGroup)이 false(권한 none)면 그 계정이 실제로 들어갈 수 있는
 *   첫 페이지(firstAccessiblePath)로 리다이렉트. 갈 곳이 하나도 없으면 NoAccess 화면.
 *   ⚠️ 예전엔 fallback="/"로 되돌렸는데, "/"는 다시 권한 가드를 통과해야 하는 곳이라
 *   CRM 권한 없는 계정이 "/"↔/crm/customers 사이를 무한 반복했다. 탈출구는 반드시
 *   "통과가 보장된 곳"이어야 한다.
 * - auth/profile 로딩은 바깥 ProtectedRoute가 끝까지 기다린다. 여기서 보는 permissions는
 *   확정값이다. 옛 서버가 permissions를 아예 주지 않는 호환 구간만 can()의 허용 폴백을 쓴다.
 *
 * 기존 ProtectedRoute/AdminRoute 안쪽에 중첩해서 쓴다. 인증·미인증 처리는
 * 바깥의 ProtectedRoute가 이미 담당하므로 여기선 "권한 등급"만 본다.
 */

import { Navigate, Outlet } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { firstAccessiblePath } from "../../lib/landing";
import NoAccess from "../../pages/OtherPage/NoAccess";
import type { PageGroupKey } from "../../../shared/rbac";

export default function RBACRoute({
  pageGroup,
}: {
  pageGroup: PageGroupKey;
}) {
  const { can } = useAuth();

  // 권한 none일 때만 차단. profile 로딩은 바깥 ProtectedRoute에서 이미 끝났다.
  if (!can(pageGroup)) {
    const landing = firstAccessiblePath(can);
    // 갈 곳이 있으면 그리로, 하나도 없으면 안내 화면(루프 방지).
    return landing ? <Navigate to={landing} replace /> : <NoAccess />;
  }

  return <Outlet />;
}
